import { VendorQuote } from "../../models/VendorQuote";
import { Fee } from "../../models/Fee";
import { VendorQuoteNote } from "../../models/VendorQuoteNote";
import { VendorItemNumber } from "../../models/VendorItemNumber";

/** Sent on every update; the server needs these to locate the quote. */
const ALWAYS_SENT_KEYS = new Set(["id", "requestedItemId"]);

/** Nested / server-managed fields compared separately (or never sent). */
const NON_SCALAR_KEYS = new Set([
  "fees",
  "notes",
  "vendorItemNumbers",
  "quoteChangeSnapshot",
  "quoteChangePendingApproval",
  "quoteChangeApprovedById",
  "quoteChangeApprovedAt",
]);

const sameValue = (a: unknown, b: unknown): boolean =>
  JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

const feeKey = (fee: Fee) => ({
  id: fee.id,
  feeType: fee.feeType,
  description: fee.description ?? "",
  vendorFee: fee.vendorFee ?? null,
  customerFee: fee.customerFee ?? null,
  qty: fee.qty ?? 1,
  isHidden: Boolean(fee.isHidden),
  isTaxable: Boolean(fee.isTaxable),
  isCreditCard: Boolean(fee.isCreditCard),
  isAdminFee:
    fee.isAdminFee == null
      ? Boolean(fee.isCreditCard)
      : Boolean(fee.isAdminFee),
  isPercentMarkup: Boolean(fee.isPercentMarkup),
  ratePercent: fee.ratePercent ?? null,
});

const notesKey = (notes: VendorQuoteNote[] | undefined) =>
  [...(notes ?? [])]
    .filter((note) => (note.noteText ?? "").trim() !== "")
    .sort(
      (a, b) =>
        (a.displayOrder ?? 0) - (b.displayOrder ?? 0) || (a.id ?? 0) - (b.id ?? 0)
    )
    .map((note) => ({
      id: note.id > 0 ? note.id : 0,
      noteType: note.noteType ?? null,
      noteText: note.noteText,
      internal: Boolean(note.internal),
    }));

const itemNumbersKey = (numbers: VendorItemNumber[] | undefined) =>
  (numbers ?? [])
    .filter((n) => (n.value ?? "").trim() !== "")
    .map((n) => `${n.type}:${n.value}`);

export const feesChanged = (
  next: Fee[] | undefined,
  baseline: Fee[] | undefined
): boolean =>
  !sameValue((next ?? []).map(feeKey), (baseline ?? []).map(feeKey));

export const notesChanged = (
  next: VendorQuoteNote[] | undefined,
  baseline: VendorQuoteNote[] | undefined
): boolean => !sameValue(notesKey(next), notesKey(baseline));

export const vendorItemNumbersChanged = (
  next: VendorItemNumber[] | undefined,
  baseline: VendorItemNumber[] | undefined
): boolean => !sameValue(itemNumbersKey(next), itemNumbersKey(baseline));

/** Pricing fields a save must round-trip; checked against the server response. */
const PRICING_KEYS = [
  "pricePerVendorUnit",
  "fixedPrice",
  "fixedPriceType",
  "unitPriceSigFig",
  "vendorQty",
  "discountQty",
  "discountAmount",
  "discountType",
  "isTaxable",
  "isCreditCard",
  "isAdminFee",
  "creditCardFeeEnabled",
] as const;

/** DB decimals round to sub-cent precision; smaller gaps are not lost edits. */
const PERSISTED_NUMBER_TOLERANCE = 0.005;

const samePersistedValue = (a: unknown, b: unknown): boolean => {
  if (typeof a === "number" || typeof b === "number") {
    const na = Number(a);
    const nb = Number(b);
    if (Number.isFinite(na) && Number.isFinite(nb)) {
      return Math.abs(na - nb) < PERSISTED_NUMBER_TOLERANCE;
    }
  }
  return sameValue(a, b);
};

const roundedFeeNumber = (value: number | null | undefined) =>
  value == null || !Number.isFinite(Number(value))
    ? null
    : Number(value).toFixed(2);

const feeContentKey = (fee: Fee) => {
  const { id: _id, ...rest } = feeKey(fee);
  return JSON.stringify({
    ...rest,
    vendorFee: roundedFeeNumber(rest.vendorFee),
    customerFee: roundedFeeNumber(rest.customerFee),
    ratePercent: roundedFeeNumber(rest.ratePercent),
  });
};

/**
 * Pricing the editor saved that the server response does not reflect.
 * Fees are compared by content (ids differ once new rows are inserted).
 */
export function unpersistedPricing(
  intended: VendorQuote,
  persisted: VendorQuote
): { keys: string[]; fees: boolean } {
  const pricingValue = (quote: VendorQuote, key: (typeof PRICING_KEYS)[number]) => {
    if (key === "isAdminFee") {
      return quote.isAdminFee == null
        ? Boolean(quote.isCreditCard)
        : Boolean(quote.isAdminFee);
    }
    return (quote as unknown as Record<string, unknown>)[key];
  };
  const keys = PRICING_KEYS.filter(
    (key) => !samePersistedValue(pricingValue(intended, key), pricingValue(persisted, key))
  );
  const fees = !sameValue(
    (intended.fees ?? []).map(feeContentKey).sort(),
    (persisted.fees ?? []).map(feeContentKey).sort()
  );
  return { keys, fees };
}

/**
 * Scalar quote fields that differ from `baseline` (plus id / requestedItemId).
 * Nested collections are excluded; use the *Changed helpers for those.
 */
export function changedVendorQuoteScalars(
  next: VendorQuote,
  baseline: VendorQuote
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  const keys = new Set([...Object.keys(next), ...Object.keys(baseline)]);
  keys.forEach((key) => {
    if (NON_SCALAR_KEYS.has(key)) {
      return;
    }
    const nextValue = (next as unknown as Record<string, unknown>)[key];
    if (
      ALWAYS_SENT_KEYS.has(key) ||
      !sameValue(
        nextValue,
        (baseline as unknown as Record<string, unknown>)[key]
      )
    ) {
      result[key] = nextValue;
    }
  });
  return result;
}
